import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import Error from '../../../components/Error';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';

const LyricsModal = ({ close, onLyricsUpdate, lyric, categories }) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);

    const isEdit = Boolean(lyric?._id);

    const [category, setCategory] = useState(() => {
        const cat = typeof lyric?.category === 'object' ? lyric.category : null;
        if (cat?._id) return { value: cat._id, label: cat.name };
        if (lyric?.category) return { value: lyric.category, label: '' }; // label doplní select options
        return null;
    });
    const [name, setName] = useState(lyric?.name || '');
    const [description, setDescription] = useState(lyric?.description || '');
    const [text, setText] = useState(lyric?.text || '');
    const [error, setError] = useState('');

    useEffect(() => { setTimeout(() => inputRef.current?.focus(), 1); }, []);

    useEffect(() => {
        // re-bind při otevření s jiným záznamem
        const cat = typeof lyric?.category === 'object' ? lyric.category : null;
        if (cat?._id) setCategory({ value: cat._id, label: cat.name });
        else if (lyric?.category) setCategory({ value: lyric.category, label: '' });
        else setCategory(null);

        setName(lyric?.name || '');
        setDescription(lyric?.description || '');
        setText(lyric?.text || '');
        setError('');
    }, [lyric]);

    const errors = useMemo(() => {
        const e = {};
        if (!category?.value) e.category = t('validation.required');
        if (!name.trim()) e.name = t('validation.required');
        if (!text.trim()) e.text = t('validation.required');
        return e;
    }, [category, name, text, t]);

    const categoryOptions = (categories || []).map(c => ({ value: c._id, label: c.name }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (Object.keys(errors).length) return;

        const payload = {
            category: category.value,
            name: name.trim(),
            description: description.trim() || undefined,
            text: text.trim()
            // createdBy řeší backend podle přihlášeného uživatele (req.user)
        };

        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.lyrics.update(lyric._id), payload);
            } else {
                await API.post(API_ENDPOINTS.lyrics.create, payload);
            }
            onLyricsUpdate?.();
            close();
        } catch (err) {
            setError(err);
            console.error('Failed to upsert lyric:', err);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="mb-2">
                {isEdit ? t('lyrics.edit') : t('lyrics.add')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Select
                    required
                    label={t('lyrics.album')}
                    placeholder={t('lyrics.album')}
                    value={category}
                    options={categoryOptions}
                    onChange={setCategory}
                    error={errors.category}
                    className="rounded-md"
                />

                <Forms.Text
                    ref={inputRef}
                    required
                    label={t('lyrics.name')}
                    placeholder={t('lyrics.name')}
                    value={name}
                    onChange={setName}
                    error={errors.name}
                    className="rounded-md"
                />

                <Forms.Textarea
                    label={t('lyrics.description')}
                    placeholder={t('lyrics.description')}
                    value={description}
                    onChange={setDescription}
                    className="rounded-md col-span-2"
                />

                <Forms.Textarea
                    required
                    label={t('lyrics.text')}
                    placeholder={t('lyrics.text')}
                    value={text}
                    onChange={setText}
                    error={errors.text}
                    maxRows={30}
                    className="rounded-md col-span-2"
                />
            </div>

            {error && <Error error={error} />}

            <Forms.Button submit label={t('save')} className="mt-4" />
        </form>
    );
};

export default LyricsModal;
